import React, { useEffect, useState } from "react";
import { Col, Row, Image, FormControl, Dropdown } from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CellBadge from "../../components/CellBadge";
import { DatePicker, Select } from "antd";
import {
  faClock,
  faMoneyBill1,
  faCircleCheck,
  faMagnifyingGlass,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { getAllChatbotSessions } from "../../redux/Actions/chatbotSessionAction";
import { Atom } from "react-loading-indicators";
import QRAnalyticsCard from "./QRAnalyticsCard";

const QrAnalytics = () => {
  const { RangePicker } = DatePicker;
  const [dateRange, setDateRange] = useState([null, null]);
  const [query, setQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [allSessionsByHotel, setAllSessionsByHotel] = useState({});
  const [sessionsTodayByHotel, setSessionsTodayByHotel] = useState({});
  const [sessionsYesterdayByHotel, setSessionsYesterdayByHotel] = useState({});
  const [hotelNamesMap, setHotelNamesMap] = useState({});

  const { loading, error, sessions, groupedSessions } = useSelector(
    (state) => state.chatbotSessions
  );

  const { listLoading: usersLoading, clients } = useSelector(
    (state) => state.client
  );

  // const clients =
  //   users && users.length > 0
  //     ? users.filter((user) => !user.isSuperAdmin && !user.isPartner)
  //     : [];

  const dispatch = useDispatch();

  const handleDateRangeChange = (dates) => {
    if (dates === null || dates[0] === null || dates[1] === null) {
      setErrorMessage(false);
      setDateRange([null, null]);
    } else {
      setDateRange(dates);
    }
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setSelectedUserId(null);
    setQuery(input);
    setErrorMessage(false);

    if (input.trim().length > 0) {
      const matches = clients.filter((user) =>
        user.companyName.toLowerCase().includes(input.toLowerCase())
      );
      setFilteredUsers(matches);
      setShowDropdown(matches.length > 0);
    } else {
      setFilteredUsers([]);
      setShowDropdown(false);
    }
  };
  const handleSelectUser = (user) => {
    setQuery(user.companyName);
    setSelectedUserId(user._id);
    setShowDropdown(false);
  };

  useEffect(() => {
    const [start, end] = dateRange;
    let startDate;
    let endDate;
    if (start && end) {
      startDate = new Date(start);
      endDate = new Date(end);
    }
    dispatch(getAllChatbotSessions(startDate, endDate, selectedUserId));
  }, [dispatch, dateRange, selectedUserId]);

  useEffect(() => {
    if (sessions) {
      const hotelMap = sessions.reduce((acc, session) => {
        acc[session.hotelId] = session.clientName;
        return acc;
      }, {});
      setHotelNamesMap(hotelMap);

      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).getTime();
      const startOfYesterday = new Date(
        today.setDate(today.getDate() - 1)
      ).getTime();
      const endOfYesterday = new Date(
        today.setHours(23, 59, 59, 999)
      ).getTime();

      // Group all sessions by hotelId
      const hotelData = sessions.reduce((acc, session) => {
        const hotelId = session.hotelId;
        acc[hotelId] = (acc[hotelId] || 0) + 1;
        return acc;
      }, {});

      setAllSessionsByHotel(
        Object.keys(hotelData).length ? hotelData : { "No Data": 1 }
      );

      // Group today's sessions by hotelId
      const todayData = sessions.reduce((acc, session) => {
        const sessionTime = new Date(session.createdAt).getTime();
        if (sessionTime >= startOfDay) {
          const hotelId = session.hotelId;
          acc[hotelId] = (acc[hotelId] || 0) + 1;
        }
        return acc;
      }, {});

      setSessionsTodayByHotel(
        Object.keys(todayData).length ? todayData : { "No Data": 1 }
      );

      // Group yesterday's sessions by hotelId
      const yesterdayData = sessions.reduce((acc, session) => {
        const sessionTime = new Date(session.createdAt).getTime();
        if (sessionTime >= startOfYesterday && sessionTime <= endOfYesterday) {
          const hotelId = session.hotelId;
          acc[hotelId] = (acc[hotelId] || 0) + 1;
        }
        return acc;
      }, {});

      setSessionsYesterdayByHotel(
        Object.keys(yesterdayData).length ? yesterdayData : { "No Data": 1 }
      );
    }
  }, [sessions]);

  const getLabelsFromIds = (ids) =>
    ids.map((id) => hotelNamesMap[id] || "Unknown Hotel");

  const pieDataAllSessions = {
    labels: getLabelsFromIds(Object.keys(allSessionsByHotel)),
    datasets: [
      {
        data: Object.values(allSessionsByHotel),
        backgroundColor: Object.keys(allSessionsByHotel).length
          ? ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"]
          : ["#E0E0E0"],
      },
    ],
  };

  const pieDataToday = {
    labels: getLabelsFromIds(Object.keys(sessionsTodayByHotel)),
    datasets: [
      {
        data: Object.values(sessionsTodayByHotel),
        backgroundColor: Object.keys(sessionsTodayByHotel).length
          ? ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"]
          : ["#E0E0E0"],
      },
    ],
  };

  const pieDataYesterday = {
    labels: getLabelsFromIds(Object.keys(sessionsYesterdayByHotel)),
    datasets: [
      {
        data: Object.values(sessionsYesterdayByHotel),
        backgroundColor: Object.keys(sessionsYesterdayByHotel).length
          ? ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"]
          : ["#E0E0E0"],
      },
    ],
  };

  const pieOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const calculateTotalSessions = (data) =>
    Object.values(data).reduce((acc, value) => acc + value, 0);

  const getServiceLabel = (service) => {
    switch (service) {
      case "maid":
        return "Attendants";
      case "valet":
        return "Valet";
      case "bellman":
        return "Bell Desk";
    }
  };
  return (
    <Row className="m-0 py-4">
      <Col>
        {/* ------------------------------------------------ FILTERS ---------------------------------------------- */}
        <Row className="align-items-center justify-content-center mb-5 p-2 rounded-2 bg-light">
          <Col xs="auto" className="">
            <h3 className="mb-0 text-primary fs-5 ">QR Scans Overview</h3>
          </Col>
          <Col xs={3} className="ms-auto">
            <RangePicker
              className="rounded-2 py-1 border-0 w-100"
              onChange={handleDateRangeChange}
            />
          </Col>
          <Col xs={2}>
            <div className="position-relative">
              <FormControl
                className="rounded-2 py-1 border-0 text-muted"
                type="text"
                value={query}
                onChange={handleInputChange}
                placeholder="Client name"
              />
              <span
                className="position-absolute top-50 end-0 translate-middle-y me-2 text-secondary"
                style={{ cursor: "pointer" }}
              >
                {query || showDropdown ? (
                  <FontAwesomeIcon
                    icon={faCircleXmark}
                    size="sm"
                    onClick={() => {
                      setQuery("");
                      setSelectedUserId(null);
                      setShowDropdown(false);
                      // setFilterApplied(false);
                    }}
                  />
                ) : (
                  <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
                )}
              </span>
            </div>
            {showDropdown && (
              <Dropdown show>
                <Dropdown.Menu
                  className="rounded-4 mt-1"
                  style={{ width: "100%" }}
                >
                  {filteredUsers.map((user, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => handleSelectUser(user)}
                    >
                      {user.companyName}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </Col>
        </Row>
        {loading ? (
          <Row className="justify-content-center align-items-center vh-100">
            <Col xs="auto">
              <Atom color="#004172" size="medium" text="" textColor="" />
            </Col>
          </Row>
        ) : (
          <>
            {/* --------------------------------------------- PIES ------------------------------------------------ */}
            <Row className="px-5 justify-content-center">
              <Col className="d-flex flex-column align-items-center">
                <h6 className="text-center">All Sessions</h6>
                <p className="text-center">
                  Total: {calculateTotalSessions(allSessionsByHotel)}
                </p>
                <div style={{ width: "200px", height: "200px" }}>
                  <Pie data={pieDataAllSessions} options={pieOptions} />
                </div>
              </Col>
              {!(dateRange[0] && dateRange[1]) && (
                <>
                  <Col className="d-flex flex-column align-items-center">
                    <h6 className="text-center">Sessions Today</h6>
                    <p className="text-center">
                      Total: {calculateTotalSessions(sessionsTodayByHotel)}
                    </p>
                    <div style={{ width: "200px", height: "200px" }}>
                      <Pie data={pieDataToday} options={pieOptions} />
                    </div>
                  </Col>
                  <Col className="d-flex flex-column align-items-center">
                    <h6 className="text-center">Sessions Yesterday</h6>
                    <p className="text-center">
                      Total: {calculateTotalSessions(sessionsYesterdayByHotel)}
                    </p>
                    <div style={{ width: "200px", height: "200px" }}>
                      <Pie data={pieDataYesterday} options={pieOptions} />
                    </div>
                  </Col>
                </>
              )}
            </Row>
            {/* -------------------------------------------- QR CODES --------------------------------------------- */}
            <Row className="mt-4 px-3">
              {groupedSessions?.map((group, index) => {
                const sessions = group.sessions || [];
                const latestSession =
                  sessions && sessions.length
                    ? [...sessions].sort(
                        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
                      )[sessions.length - 1]
                    : null;
                const totalTipAmount = sessions
                  .filter(
                    (s) =>
                      s.stage?.paymentProcesed &&
                      typeof s.tipAmount === "number"
                  )
                  .reduce((sum, s) => sum + s.tipAmount, 0);

                const tipEnteredSessions = sessions.filter(
                  (s) => s.stage?.tipSelected
                );

                const successFullSessions = sessions.filter(
                  (s) => s.stage?.paymentProcesed
                );

                const stats = {
                  totalSessions: sessions.length,
                  enteredTip: sessions.filter((s) => s.stage?.tipSelected)
                    .length,
                  teamTipOptionSelected: sessions.filter(
                    (s) => s.stage?.teamTipSelected == true
                  ).length,
                  directTipOptionSelected: sessions.filter(
                    (s) => s.stage?.directTipSelected == true
                  ).length,
                  successfullyPaid: sessions.filter(
                    (s) => s.stage?.paymentProcesed
                  ).length,
                  roomNumberEntered: sessions.filter(
                    (s) => s.stage?.roomNumberEntered
                  ).length,
                  maidNameEntered: sessions.filter(
                    (s) => s.stage?.maidNameEntered
                  ).length,
                  reviewAdded: sessions.filter((s) => s.stage?.reviewAdded)
                    .length,
                  processingFeeConfirmed: sessions.filter(
                    (s) => s.stage?.processingFeeConfirmed
                  ).length,

                  // Only count feePaid/FeeDeclined among tip-entered sessions
                  feePaid: tipEnteredSessions.filter(
                    (s) => s.feePaidByCustomer === true
                  ).length,
                  feeDeclined: tipEnteredSessions.filter(
                    (s) => s.feePaidByCustomer === false
                  ).length,

                  qrScans: sessions.length,
                  poolTips: successFullSessions.filter((s) => s.teamTip == true)
                    .length,
                  directTips: successFullSessions.filter(
                    (s) => s.teamTip == false
                  ).length,
                  totalTipAmount,
                };

                return (
                  <Col key={index} xs={12} className="mb-4">
                    <QRAnalyticsCard
                      hotelName={latestSession?.clientName || "Unknown Hotel"}
                      clientModel={latestSession?.clientModel}
                      qrImageUrl={latestSession?.qrCode}
                      locationName={latestSession?.location}
                      serviceType={latestSession?.service || "N/A"}
                      flowType={latestSession?.flow || "N/A"}
                      stats={stats}
                    />
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </Col>
    </Row>
  );
};

export default QrAnalytics;
